/**
 * SubscriptionSettings Page - Sprint 25
 * 
 * Admin Settings → Subscription page showing:
 * - Current plan details (Your Plan card)
 * - Billing status with badge
 * - Plan change history table
 * - Billing notes editor
 * - Seat usage and management
 * - Feature entitlements
 * - Upgrade options (no Stripe)
 * 
 * Route: /settings/subscription
 */

import React, { useState, useEffect } from 'react';
import { 
  Crown, 
  Users, 
  CreditCard, 
  ArrowUpRight, 
  Check, 
  X, 
  RefreshCw,
  ChevronRight,
  Building2,
  AlertCircle,
  Sparkles,
  History
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import YourPlanCard from '../components/billing/YourPlanCard';
import BillingStatusBadge from '../components/billing/BillingStatusBadge';
import PlanChangeHistory from '../components/billing/PlanChangeHistory';
import BillingNotesEditor from '../components/billing/BillingNotesEditor';

// Plan comparison data
const PLAN_COMPARISON = [
  { feature: 'Core PM & Scoping', team: true, business: true, enterprise: true, enterprise_plus: true, unlimited: true },
  { feature: 'Resource Capacity', team: false, business: true, enterprise: true, enterprise_plus: true, unlimited: true },
  { feature: 'Demand Management', team: false, business: true, enterprise: true, enterprise_plus: true, unlimited: true },
  { feature: 'AI Copilot (Basic)', team: false, business: true, enterprise: true, enterprise_plus: true, unlimited: true },
  { feature: 'AI Copilot (Full)', team: false, business: false, enterprise: true, enterprise_plus: true, unlimited: true },
  { feature: 'AI Agents', team: false, business: false, enterprise: false, enterprise_plus: true, unlimited: true },
  { feature: 'Jira/ADO Standard', team: false, business: true, enterprise: true, enterprise_plus: true, unlimited: true },
  { feature: 'Jira/ADO Full Sync', team: false, business: false, enterprise: true, enterprise_plus: true, unlimited: true },
  { feature: 'Workflow Builder', team: false, business: false, enterprise: true, enterprise_plus: true, unlimited: true },
  { feature: 'SSO/MFA', team: false, business: 'addon', enterprise: true, enterprise_plus: true, unlimited: true },
  { feature: 'Power BI', team: false, business: false, enterprise: true, enterprise_plus: true, unlimited: true },
  { feature: 'Data Residency', team: false, business: false, enterprise: false, enterprise_plus: true, unlimited: true },
  { feature: '99.9% SLA', team: false, business: false, enterprise: true, enterprise_plus: true, unlimited: true },
  { feature: 'Dedicated CSM', team: false, business: false, enterprise: true, enterprise_plus: true, unlimited: true },
];

const TIER_COLORS = {
  'team': 'border-slate-300 bg-slate-50',
  'business': 'border-blue-300 bg-blue-50',
  'enterprise': 'border-purple-300 bg-purple-50',
  'enterprise_plus': 'border-amber-300 bg-amber-50',
  'unlimited_enterprise': 'border-emerald-300 bg-emerald-50',
};

const SubscriptionSettings = () => {
  const { user, tenant, tenantId: authTenantId } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [seatUsage, setSeatUsage] = useState(null);
  const [billingDetails, setBillingDetails] = useState(null);
  const [billingNotes, setBillingNotes] = useState('');

  // Use tenantId from auth context or fallback to tenant.id
  const tenantId = authTenantId || tenant?.id;

  useEffect(() => {
    if (tenantId && tenantId !== 'default-tenant') {
      loadData();
    } else {
      setLoading(false);
    }
  }, [tenantId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [plansRes, seatsRes, billingRes, notesRes] = await Promise.all([
        api.get('/billing/plans'),
        api.get(`/billing/tenant/${tenantId}/seats`),
        api.get(`/billing/tenant/${tenantId}/billing-details`),
        api.get(`/billing/tenant/${tenantId}/billing-notes`)
      ]);
      
      setPlans(plansRes.data.plans || []);
      setSeatUsage(seatsRes.data);
      setBillingDetails(billingRes.data);
      setBillingNotes(notesRes.data.billing_notes || '');
      
      // Find current plan
      const currentTier = seatsRes.data.tier;
      const current = plansRes.data.plans?.find(p => p.tier === currentTier);
      setCurrentPlan(current);
    } catch (err) {
      console.error('Failed to load subscription data:', err);
      toast.error('Failed to load subscription information');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeClick = () => {
    setActiveTab('plans');
  };

  const handleSelectPlan = (tier) => {
    // In a real implementation, this would open a checkout flow
    toast.info(`Upgrade to ${tier} - Coming soon!`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="subscription-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-purple-600" />
            Subscription Settings
          </h1>
          <p className="text-gray-500 mt-1">Manage your plan, seats, and billing</p>
        </div>
        <Button variant="outline" onClick={loadData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="plans">Plans</TabsTrigger>
          <TabsTrigger value="usage">Usage</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6 mt-6">
          {/* Billing Status Header */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
            <div className="flex items-center gap-3">
              <CreditCard className="w-5 h-5 text-gray-500" />
              <div>
                <span className="text-sm text-gray-600">Billing Status</span>
                <div className="mt-1">
                  <BillingStatusBadge 
                    status={billingDetails?.billing_status || 'manual_billing'} 
                  />
                </div>
              </div>
            </div>
            <div className="text-right text-sm text-gray-500">
              <div>Last updated</div>
              <div>{billingDetails?.last_updated ? new Date(billingDetails.last_updated).toLocaleDateString() : 'N/A'}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Your Plan Card */}
            <YourPlanCard 
              tenantId={tenantId} 
              onUpgradeClick={handleUpgradeClick}
            />

            {/* Quick Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  Organization Overview
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="text-2xl font-bold text-gray-900">
                      {seatUsage?.current_users || 0}
                    </div>
                    <div className="text-sm text-gray-500">Active Users</div>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="text-2xl font-bold text-gray-900">
                      {seatUsage?.seat_limit === 'unlimited' ? '∞' : seatUsage?.seat_limit || 0}
                    </div>
                    <div className="text-sm text-gray-500">Seat Limit</div>
                  </div>
                </div>

                {seatUsage?.warning_level && (
                  <div className={`p-4 rounded-lg flex items-start gap-3 ${
                    seatUsage.warning_level === 'limit_reached' 
                      ? 'bg-red-50 border border-red-200' 
                      : 'bg-amber-50 border border-amber-200'
                  }`}>
                    <AlertCircle className={`w-5 h-5 ${
                      seatUsage.warning_level === 'limit_reached' 
                        ? 'text-red-600' 
                        : 'text-amber-600'
                    }`} />
                    <div>
                      <p className={`font-medium ${
                        seatUsage.warning_level === 'limit_reached' 
                          ? 'text-red-800' 
                          : 'text-amber-800'
                      }`}>
                        {seatUsage.warning_level === 'limit_reached' 
                          ? 'Seat Limit Reached' 
                          : 'Approaching Seat Limit'}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">{seatUsage.message}</p>
                    </div>
                  </div>
                )}

                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => window.location.href = '/admin/users'}
                >
                  <Users className="w-4 h-4 mr-2" />
                  Manage Users
                  <ChevronRight className="w-4 h-4 ml-auto" />
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Billing Notes */}
          <BillingNotesEditor 
            tenantId={tenantId}
            initialNotes={billingNotes}
            onUpdate={(newNotes) => setBillingNotes(newNotes)}
          />

          {/* Plan Change History */}
          <PlanChangeHistory tenantId={tenantId} limit={10} />
        </TabsContent>

        {/* Plans Tab */}
        <TabsContent value="plans" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {plans.map((plan) => {
              const isCurrentPlan = plan.tier === seatUsage?.tier;
              const isHigherTier = plans.indexOf(plan) > plans.findIndex(p => p.tier === seatUsage?.tier);
              
              return (
                <Card 
                  key={plan.tier}
                  className={`relative ${TIER_COLORS[plan.tier] || ''} ${
                    isCurrentPlan ? 'ring-2 ring-purple-500' : ''
                  }`}
                >
                  {isCurrentPlan && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-purple-600">Current Plan</Badge>
                    </div>
                  )}
                  
                  <CardHeader className="text-center pb-2">
                    <CardTitle className="text-lg">{plan.name}</CardTitle>
                    <CardDescription>
                      Up to {plan.seat_limit === 'Unlimited' ? '∞' : plan.seat_limit} users
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent className="space-y-4">
                    {/* Highlights */}
                    <ul className="space-y-2 text-sm">
                      {plan.highlight_features?.slice(0, 4).map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA */}
                    {isCurrentPlan ? (
                      <Button variant="outline" disabled className="w-full">
                        Current Plan
                      </Button>
                    ) : isHigherTier ? (
                      <Button 
                        className="w-full bg-gradient-to-r from-purple-600 to-indigo-600"
                        onClick={() => handleSelectPlan(plan.tier)}
                      >
                        Upgrade
                        <ArrowUpRight className="w-4 h-4 ml-2" />
                      </Button>
                    ) : (
                      <Button variant="outline" disabled className="w-full text-gray-400">
                        Lower Tier
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Feature Comparison Table */}
          <Card className="mt-8">
            <CardHeader>
              <CardTitle>Feature Comparison</CardTitle>
              <CardDescription>Compare features across all plans</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Feature</th>
                      <th className="text-center py-3 px-4">Team</th>
                      <th className="text-center py-3 px-4">Business</th>
                      <th className="text-center py-3 px-4">Enterprise</th>
                      <th className="text-center py-3 px-4">Enterprise+</th>
                      <th className="text-center py-3 px-4">Unlimited</th>
                    </tr>
                  </thead>
                  <tbody>
                    {PLAN_COMPARISON.map((row, idx) => (
                      <tr key={idx} className="border-b last:border-0 hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium">{row.feature}</td>
                        {['team', 'business', 'enterprise', 'enterprise_plus', 'unlimited'].map((tier) => (
                          <td key={tier} className="text-center py-3 px-4">
                            {row[tier] === true ? (
                              <Check className="w-5 h-5 text-emerald-600 mx-auto" />
                            ) : row[tier] === 'addon' ? (
                              <Badge variant="outline" className="text-xs">Add-on</Badge>
                            ) : (
                              <X className="w-5 h-5 text-gray-300 mx-auto" />
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Usage Tab */}
        <TabsContent value="usage" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Seat Usage Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  Seat Usage
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Active Users</span>
                  <span className="font-bold text-2xl">{seatUsage?.current_users || 0}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Seat Limit</span>
                  <span className="font-bold text-2xl">
                    {seatUsage?.seat_limit === 'unlimited' ? '∞' : seatUsage?.seat_limit}
                  </span>
                </div>
                
                {seatUsage?.seat_limit !== 'unlimited' && (
                  <div className="space-y-2">
                    <Progress 
                      value={seatUsage?.usage_percent || 0} 
                      className="h-3"
                    />
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>{seatUsage?.usage_percent?.toFixed(1)}% used</span>
                      <span>
                        {Math.max(0, (seatUsage?.seat_limit || 0) - (seatUsage?.current_users || 0))} available
                      </span>
                    </div>
                  </div>
                )}

                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-2">Seat Thresholds</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                      <span>80% - Warning notification</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                      <span>95% - Critical warning</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <span>100% - New users blocked</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Feature Usage */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                  Feature Entitlements
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-500 mb-4">
                  Features available on your {currentPlan?.name || 'current'} plan
                </p>
                <div className="space-y-2">
                  {currentPlan?.highlight_features?.map((feature, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center gap-2 p-2 bg-emerald-50 rounded-md"
                    >
                      <Check className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <Button 
                  variant="outline" 
                  className="w-full mt-4"
                  onClick={() => setActiveTab('plans')}
                >
                  View All Features
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SubscriptionSettings;
